import { CheckCircleIcon, ArrowRightIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { JobApplication } from "@type/job";
import { parseFormat } from "@util/date";
import { trackEvent } from "@util/mixpanel";

interface Props {
  job_application: JobApplication | undefined;
}

export default function Complete(props: Props) {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4 sm:p-6">
      <div className="text-center mb-4 sm:mb-8">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-2 sm:mb-4">
          <CheckCircleIcon className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold">
          Application Successful.
        </h2>
        <p className="text-gray-600 mt-2">
          Your application for {props.job_application?.job_title || ""} at <b>{props.job_application?.company_name}</b> has been received.
        </p>
      </div>

      <div className="bg-gray-50 rounded-lg p-4 sm:p-6 mb-4 sm:mb-8">
        <h3 className="text-lg font-medium mb-4">Application Details</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-white rounded-lg border border-gray-200">
            <p className="text-sm text-gray-500">Job Title</p>
            <p className="font-medium">
              {props.job_application?.job_title || ""}
            </p>
          </div>

          <div className="p-4 bg-white rounded-lg border border-gray-200">
            <p className="text-sm text-gray-500">Company</p>
            <p className="font-medium">
              {props.job_application?.company_name || ""}
            </p>
          </div>

          <div className="p-4 bg-white rounded-lg border border-gray-200">
            <p className="text-sm text-gray-500">Application ID</p>
            <p className="font-medium">
              {props.job_application?.applicant_id || ""}
            </p>
          </div>

          <div className="p-4 bg-white rounded-lg border border-gray-200">
            <p className="text-sm text-gray-500">Submitted Date</p>
            <p className="font-medium">
              {parseFormat(
                props.job_application?.created_at || "",
                "D MMMM YYYY"
              )}
            </p>
          </div>
        </div>

        <div className="mt-6 bg-[#E7FFE6] border border-[#C5F5C4] rounded-lg p-4">
          <h4 className="font-medium text-[#4C7F4B] mb-2">What happens next?</h4>
          <ul className="space-y-2 text-[#4C7F4B] text-sm m-0">
            <li className="flex items-start m-0">
              <span className="flex-shrink-0 w-5 h-5 bg-[#C5F5C4] border border-[#4C7F4B] rounded-full flex items-center justify-center text-[#4C7F4B] mr-2">
                1
              </span>
              {props.job_application?.company_name} will review your application.
            </li>
            <li className="flex items-start m-0">
              <span className="flex-shrink-0 w-5 h-5 bg-[#C5F5C4] border border-[#4C7F4B] rounded-full flex items-center justify-center text-[#4C7F4B] mr-2">
                2
              </span>
              Once your application has been approved, the company will reach
              out to you for further actions.
            </li>
            <li className="flex items-start m-0">
              <span className="flex-shrink-0 w-5 h-5 bg-[#C5F5C4] border border-[#4C7F4B] rounded-full flex items-center justify-center text-[#4C7F4B] mr-2">
                3
              </span>
              You may track your application status on your dashboard.
            </li>
          </ul>
        </div>
      </div>

      <div className="flex justify-center">
        <button
          className={`px-6 py-3 bg-blue-700 hover:bg-blue-800 text-white rounded-md  transition-colors flex items-center`}
          onClick={() => {
            trackEvent("application_dashboard_viewed")
            trackEvent("application_assessment_completed", {
              candidate_id: "",
              job_id: props.job_application?.job_id || "",
              job_title: props.job_application?.job_title || "",
              company_name: props.job_application?.company_name || "",
              job_application_status: "Pending Review",
              applied_job_count: props.job_application?.applied_job_count || 0,
              multi_apply: (props.job_application?.applied_job_count || 0) > 0 ? true : false,
            });
            navigate("/dashboard")
          }}
        >
          Go to Dashboard
          <ArrowRightIcon className="ml-2 w-4 h-4" />
        </button>
      </div>
    </div >
  );
}
